// 成員選單的 <option> 清單 — 會把「載入中 / 失敗 / 真的沒人」講出來。
//
// 2026-08-28 使用者回報「直屬主管根本選不到」。 追的時候發現一個更根本的缺陷:
// 全部 11 個成員下拉都是這樣寫的 ——
//
//     <option value="">— 選成員 —</option>
//     {staffQ.data?.map(...)}
//
// 於是「還在載入」「請求失敗」「真的沒有其他在職成員」三種情況**畫面長得一模一樣**:
// 一個只有 placeholder 的下拉。 使用者看到的是「選不到」, 而且沒有任何線索說為什麼;
// 我自己查這個 bug 時也因此多繞了好幾輪 —— 從畫面上分不出是資料沒來還是真的沒人。
//
// 這支不解決「為什麼載不到」, 它解決的是「載不到的時候沒有人知道」。
//
// ⚠ 抽成共用元件而不是在 11 個地方各寫一次 —— 那正是這個 repo 反覆漂移的形狀
//   (教訓 4)。 呼叫端一行replace, 三種狀態的文案只有這裡一份。

import type { StaffMember } from '../../lib/use-approvals.ts';
import { staffLabel } from '../../lib/use-approvals.ts';

/**
 * 只取 useQuery 結果裡真正需要的三個欄位 —— 這樣「把陣列往下傳」的呼叫端
 * (報帳/請假/出差的 *Document) 也能自己組一個，不必把整個 UseQueryResult 型別
 * 拉進那些純顯示元件。
 */
export interface StaffQueryLike {
  data?: StaffMember[];
  isPending: boolean;
  isError: boolean;
}

export function StaffOptions({
  q,
  placeholder,
  exclude,
}: {
  q: StaffQueryLike;
  /**
   * 第一個選項, 例如「— 無 —」「— 選成員 —」「本人(申請者)」。
   *
   * ⚠ **永遠會 render**, 狀態訊息是附加在後面的 disabled 選項 —— 因為這一格
   * 對某些呼叫端**是有意義的值不是佔位字**: 報帳的「本人(申請者)」= 退款給自己
   * (最常見的情況), 請假/出差的「— 不指定 —」= 不設代理人。 把它換成「載入中…」
   * 會讓一個正常選項在載入/失敗時憑空消失。
   */
  placeholder: string;
  /** 要從清單排除的人 (通常是本人 — 不能當自己的主管/代理人)。 */
  exclude?: number | string | null;
}) {
  const rows = (q.data ?? []).filter((s) => exclude == null || String(s.id) !== String(exclude));

  // 三種「清單是空的」各自說出原因。 disabled → 選不到, 只是說明文字。
  const note = q.isPending
    ? '載入中…'
    : q.isError
      ? '⚠ 無法載入成員清單，請重新整理'
      : rows.length === 0
        ? '查無其他在職成員'
        : null;

  return (
    <>
      <option value="">{placeholder}</option>
      {note && (
        <option value="" disabled>
          {note}
        </option>
      )}
      {rows.map((s) => (
        <option key={s.id} value={s.id}>
          {staffLabel(s)}
        </option>
      ))}
    </>
  );
}
